// A arte oficial do Luy (a mesma do Cais, UY3.Agents.Cais/src/mascot) preparada para entrar inline.
// O desenho é rígido (00-plano §9); só a paleta sai de token: `--lume-luy-mark-*` e, no erro, os de perigo.

/** `face`: só o rosto, para 20–32 px, onde o anel de status vira ruído (medição do Cais, 13/09/2026). */
export type LuyCrop = 'full' | 'face';

const FACE_VIEWBOX = '-2 -78 1100 1100';

const MARK: Readonly<Record<string, string>> = {
  luz: 'var(--lume-luy-mark-light)',
  base: 'var(--lume-luy-mark-base)',
  sombra: 'var(--lume-luy-mark-shade)',
};

// O erro é sinal semântico: a mesma geometria com a paleta de perigo do Lume (o Cais recolore em vermelho).
const DANGER: Readonly<Record<string, string>> = {
  luz: 'var(--lume-error)',
  base: 'var(--lume-danger)',
  sombra: 'var(--lume-danger-hover)',
};

// O brilho atrás do anel (o "bloom" do export de design), com as cores da paleta em vez de hex fixo.
const corona = (palette: Readonly<Record<string, string>>) =>
  `<g id="luy-corona" filter="url(#luy-bloom)" stroke="none">` +
  `<circle cx="550" cy="485" r="612" style="fill:${palette['luz'] ?? ''}" opacity="0.35"/>` +
  `<circle data-orbit="0" cx="702.5" cy="425.9" r="480" style="fill:${palette['sombra'] ?? ''}" opacity="0.5"/>` +
  `<circle data-orbit="1" cx="628.9" cy="647.6" r="432" style="fill:${palette['base'] ?? ''}" opacity="0.55"/>` +
  `<circle data-orbit="2" cx="399.8" cy="558.8" r="461" style="fill:${palette['luz'] ?? ''}" opacity="0.5"/>` +
  `<circle data-orbit="3" cx="457.7" cy="333.6" r="528" style="fill:${palette['luz'] ?? ''}" opacity="0.4"/></g>`;

/** Prepara o SVG: caixa do componente, paleta por token, ids únicos por cópia e cabeça agrupada para respirar. */
export function prepareLuyArt(
  svg: string,
  prefix: string,
  options: { readonly crop?: LuyCrop; readonly danger?: boolean } = {},
): string {
  const crop = options.crop ?? 'face';
  const palette = options.danger ? DANGER : MARK;
  let output = svg
    .replace(/<\?xml[^>]*\?>\s*/, '')
    .replace(
      /(<stop\b[^>]*\sid="luy-cor-(luz|base|sombra)"[^>]*?)\sstop-color="[^"]*"/g,
      (_m, head: string, stop: string) => `${head} style="stop-color:${palette[stop] ?? MARK['base'] ?? ''}"`,
    )
    .replace(/(<svg\b[^>]*?)\swidth="[^"]*"/, '$1 width="100%"')
    .replace(/(<svg\b[^>]*?)\sheight="[^"]*"/, '$1 height="100%"')
    .replace(/<svg\b/, '<svg preserveAspectRatio="xMidYMid meet" style="display:block;overflow:visible" aria-hidden="true"')
    .replace(/<title\b[^>]*>.*?<\/title>/s, '')
    .replace(/<desc\b[^>]*>.*?<\/desc>/s, '')
    .replace(/\saria-labelledby="[^"]*"/, '')
    .replace(/<rect\b[^>]*id="luy-sobrancelha-esquerda"[^>]*\/>/, '<path id="luy-sobrancelha-esquerda" d="M372 382 Q430 356 488 372" fill="none" stroke="url(#luy-leds)" stroke-width="26" stroke-linecap="round"/>')
    .replace(/<rect\b[^>]*id="luy-sobrancelha-direita"[^>]*\/>/, '<path id="luy-sobrancelha-direita" d="M632 372 Q690 356 748 382" fill="none" stroke="url(#luy-leds)" stroke-width="26" stroke-linecap="round"/>');
  if (crop === 'face') {
    output = output
      .replace(/(<svg\b[^>]*?)\sviewBox="[^"]*"/, `$1 viewBox="${FACE_VIEWBOX}"`)
      .replace(/(<g\b[^>]*\sid="luy-status")/, '$1 display="none"');
  } else {
    output = output
      .replace('</defs>', '<filter id="luy-bloom" x="-60%" y="-60%" width="220%" height="220%" color-interpolation-filters="sRGB"><feGaussianBlur stdDeviation="112"/></filter></defs>')
      .replace(/(<g\b[^>]*\sid="luy-status"[^>]*>)/, `$1${corona(palette)}`);
  }
  return output
    .replace(/(<g\b[^>]*\sid="luy-corpo")/, '<g data-luy="cabeca">$1')
    .replace(/<\/svg>\s*$/, '</g></svg>')
    .replace(/\sid="([^"]+)"/g, (_m, id: string) => ` id="${prefix}-${id}"`)
    .replace(/url\(#([^)]+)\)/g, (_m, id: string) => `url(#${prefix}-${id})`)
    .replace(/(\bhref)="#([^"]+)"/g, (_m, attr: string, id: string) => `${attr}="#${prefix}-${id}"`);
}
